import { DateTime, Duration } from 'luxon';
import { DailySummary, MeetingLog, User, Worklog } from 'src/models';
import { DateRange } from '../common/daterange';
import { TimeService } from '../services/time';
import { getIntegrationProjectName } from './integration-helpers';
import { minMeetingSeconds } from './meeting-helpers';

const emptyWlTemplate: WorklogExtended = {
  mode: 'offline',
  projectId: null,
  taskId: null,
  projectName: null,
  taskName: null,
  deviceId: '',
  end: '',
  start: '',
  offset: '',
  reason: '',
  timezone: null,
  time: 0,
};

export enum TimelineStatusCodes {
  Tracking = 0,
  Break = 1,
  Offline = 2,
  NeverTracked = 3,
  NotActive = 4,
  Leave = 9,
  UnpaidLeave = 10,
}

export interface WorklogDetails {
  start: Date;
  end: Date;
  total: number;
  worked: number;
}

export interface WorklogUserDetails {
  status: TimelineStatusCodes;
  duration: Duration;
  task: string;
  taskId: string;
  projectId: string;
  projectName: string;
}

export interface TimelineData {
  user: User;
  timezone: string;
  offset: string;
  userDetails: WorklogUserDetails;
  worklog: Worklog[];
  details: WorklogDetails;
}

export interface WorklogExtended extends Worklog {
  end: string;
  offset: string;
  timezone: string;
}

export interface EditTimeData {
  start: number;
  end: number;
  project: { id: string, name: string };
  task: { id: string, name: string, project: { id: string, name: string } };
  min: number;
  max: number;
  reason?: string;
}

export interface TimelineDataExtended extends TimelineData {
  worklog: WorklogExtended[];
}

export interface WeeklyTimelineData {
  user: User;
  timezone: string;
  userDetails: WorklogUserDetails;
  weeklySummary: DailySummary[];
  details: { worked: number };
}

/** Worked seconds for a weekly timeline row. Unpaid fields are often omitted (0) in mock/API payloads. */
export function getWeeklyWorkedSeconds(summaries: Array<DailySummary | null | undefined> | null | undefined): number {
  if (!summaries?.length) {
    return 0;
  }

  return summaries.reduce((acc, sm) => {
    const total = sm?.total || 0;
    const unpaid = (sm?.unpaidLeave || 0) + (sm?.unpaidBreak || 0);
    return acc + Math.max(0, total - unpaid);
  }, 0);
}

export function getWorklogDetails(wls: Worklog[]): WorklogDetails {
  wls = wls.filter(x => !['unpaidBreak', 'unpaidLeave'].includes(x.mode));
  const start = wls.map(x => DateTime.fromISO(x.start)).reduce((acc, x) => x > acc ? acc : x, undefined);
  const end = wls.map(x => DateTime.fromISO(x.start).plus({ second: x.time })).reduce((acc, x) => x < acc ? acc : x, undefined);
  const worked = wls.filter(wl => !['pending', 'disapproved'].includes(wl.approvalStatus) && wl.mode !== 'unpaidBreak').reduce((acc, x) => acc + x.time, 0);
  const total = end && start && end.diff(start, 'seconds').seconds || 0;

  return {
    start: start?.toJSDate(),
    end: start && end.toJSDate(),
    total,
    worked,
  };
}

export function getUserDetails(user: User): WorklogUserDetails {
  const lt = user.lastTrack;
  let status = TimelineStatusCodes.Offline;
  const duration = lt ? DateTime.fromISO(lt.activeAt || lt.updatedAt).diffNow('seconds').negate() : Duration.fromMillis(0);
  const task = lt?.taskName;
  const taskId = lt?.taskId;
  const projectName = lt?.projectName;
  const projectId = lt?.projectId;

  if (!user.active) {
    status = TimelineStatusCodes.NotActive;
  } else if (!lt) {
    status = TimelineStatusCodes.NeverTracked;
  } else if (lt.online && ['computer', 'mobile'].includes(lt.status)) {
    status = TimelineStatusCodes.Tracking;
  } else if (lt.online && ['paidBreak', 'unpaidBreak'].includes(lt.status)) {
    status = TimelineStatusCodes.Break;
  } else if (lt.status === 'paidLeave') {
    status = TimelineStatusCodes.Leave;
  } else if (lt.status === 'unpaidLeave') {
    status = TimelineStatusCodes.UnpaidLeave;
  }

  return { status, duration, task, taskId, projectName, projectId };
}

export function constrainWorklogInDaterange(wl: Worklog, range: DateRange): Worklog {
  const start = DateTime.fromISO(wl.start);
  const end = start.plus({ seconds: wl.time });
  const containsStart = range.contains(start);
  const containsEnd = range.contains(end);

  if (containsStart && containsEnd) { return wl; }

  const newStart = DateTime.max(start, range.start);
  const newEnd = DateTime.min(end, range.end);
  const diff = Math.max(0, newEnd.diff(newStart, 'seconds').seconds);

  return { ...wl, start: newStart.toUTC().toISO(), time: diff };
}

export function constrainMeetingInDaterange(meeting: MeetingLog, range: DateRange): MeetingLog {
  const start = DateTime.fromISO(meeting.start);
  const end = start.plus({ seconds: meeting.time });
  const containsStart = range.contains(start);
  const containsEnd = range.contains(end);

  if (containsStart && containsEnd) { return meeting; }

  const newStart = DateTime.max(start, range.start);
  const newEnd = DateTime.min(end, range.end);
  const diff = Math.max(0, newEnd.diff(newStart, 'seconds').seconds);

  return { ...meeting, start: newStart.toUTC().toISO(), time: diff };
}

export function getExtendedWorklog(wls: Worklog[], timezone: string, day: DateTime): WorklogExtended[] {
  const starts = wls.map(x => DateTime.fromISO(x.start));
  const ends = wls.map(x => DateTime.fromISO(x.start).plus({ second: x.time }));
  const start = (starts.reduce((acc, x) => x > acc ? acc : x, undefined) || day).setZone(timezone);
  const end = (ends.reduce((acc, x) => x < acc ? acc : x, undefined) || day).setZone(timezone);

  const firstDay = start.startOf('day');
  const nextDay = firstDay.plus({ day: 1 });
  const endDay = (+end.startOf('day') === +end) ? end : TimeService.endOfExclusive(end, 'day');
  const lastDay = DateTime.max(nextDay, endDay);
  const lastDayDiff = lastDay.diff(firstDay, 'seconds').seconds;

  const res: WorklogExtended[] = [];

  const offset = DateTime.utc().setZone(timezone).zone.formatOffset(start.toMillis(), 'techie');

  for (const wl of wls) {
    const startIso = DateTime.fromISO(wl.start);
    const startDiff = startIso.diff(firstDay, 'seconds').seconds;
    const endDiff = startDiff + wl.time;
    const endIso = firstDay.plus({ seconds: endDiff });

    const prevEnd = (res[res.length - 1] || { end: firstDay.toUTC().toISO() }).end;
    const prevEndDiff = DateTime.fromISO(prevEnd).diff(firstDay, 'seconds').seconds;
    const emptyDiff = startDiff - prevEndDiff;

    // prefix integration name to project names
    const projectName = getIntegrationProjectName({ id: wl.projectId, name: wl.projectName });

    if (emptyDiff > 0) {
      const emptyWl: WorklogExtended = {
        ...emptyWlTemplate,
        projectName,
        deviceId: wl.deviceId,
        start: prevEnd,
        end: wl.start,
        time: emptyDiff,
        timezone,
        offset,
      };

      res.push(emptyWl);
    }

    const wlx: WorklogExtended = {
      ...wl,
      projectName,
      end: endIso.toUTC().toISO(),
      timezone,
      offset,
    };
    res.push(wlx);
  }



  const last = res[res.length - 1];
  const lastEnd = last ? last.end : firstDay.toUTC().toISO();
  const lastEndDiff = DateTime.fromISO(lastEnd).diff(firstDay, 'seconds').seconds;
  const lastEmptyDiff = lastDayDiff - lastEndDiff;
  if (lastEmptyDiff > 0) {
    const emptyWl: WorklogExtended = {
      ...emptyWlTemplate,
      deviceId: last?.deviceId || '',
      start: lastEnd,
      end: lastDay.toUTC().toISO(),
      time: lastEmptyDiff,
      timezone,
      offset,
    };

    res.push(emptyWl);
  }


  return res;
}

export function diffSinceDayStart(time: string, zone: string) {
  const dt = DateTime.fromISO(time, { zone });
  return dt.diff(dt.startOf('day'), 'seconds').seconds;
}

/**
 * This function is a workaround of 'exclude-fields=shift' param for the worklog socket update.
 * We are able to use 'exclude-fields=shift' on the API side but it will not work with the socket.
 * Therefore we need to fix the wrong shift related behavior of the socket on the front-end side.
 * The function tries to find if the new worklog is a part of the existing worklog and
 * If so, it will extend it or it will push the new worklog to the list.
 * See STAFF-129610 for more details
 * @param userWorklogs target user worklog list
 * @param newWorklog new worklog to add
 */
/**
 * Maps a meeting range (from `activity/meetinglog`) to a `Worklog` with
 * `mode: 'meeting'` so it can be rendered on the timeline like any other block.
 * The original meeting range is preserved on `meeting` for the detail panel.
 *
 * Meeting logs carry no project/task of their own (the backend builds them from
 * microphone usage), so the block inherits the project/task from the worklog it
 * overlaps (`source`) to show the work happening during the meeting (STAFF-145177).
 */
export function meetingToWorklog(meeting: MeetingLog, source?: Worklog): Worklog {
  return {
    deviceId: '',
    mode: 'meeting',
    projectId: source?.projectId ?? null,
    projectName: source?.projectName ?? null,
    taskId: source?.taskId ?? null,
    taskName: source?.taskName ?? null,
    start: meeting.start,
    time: meeting.time,
    userId: meeting.userId,
    meeting,
  };
}

interface WorklogBounds { wl: Worklog, start: number, end: number }

/**
 * Picks the worklog that overlaps the meeting interval the most and carries a
 * project or task, so the meeting block can inherit it (STAFF-145177). Returns
 * null when no overlapping worklog has a project/task (e.g. meeting over
 * break/offline time).
 */
function pickOverlappingWorklog(startMs: number, endMs: number, bounds: WorklogBounds[]): Worklog {
  let best: Worklog = null;
  let bestOverlap = 0;

  for (const b of bounds) {
    const overlap = Math.min(b.end, endMs) - Math.max(b.start, startMs);
    if (overlap > bestOverlap && (b.wl.projectId || b.wl.projectName || b.wl.taskId || b.wl.taskName)) {
      bestOverlap = overlap;
      best = b.wl;
    }
  }

  return best;
}

function sliceWorklog(wl: Worklog, startMs: number, endMs: number): Worklog {
  const origStart = DateTime.fromISO(wl.start).toMillis();
  const origEnd = origStart + (wl.time * 1000);
  if (startMs === origStart && endMs === origEnd) { return wl; }

  return {
    ...wl,
    start: DateTime.fromMillis(startMs).toUTC().toISO(),
    time: (endMs - startMs) / 1000,
  };
}

/**
 * Returns the portions of a worklog segment that are NOT covered by any of the
 * given meeting intervals (carve-out). Produces 0, 1 or more sub-segments.
 */
function subtractMeetingIntervals(wl: Worklog, wStart: number, wEnd: number, intervals: Array<{ start: number, end: number }>): Worklog[] {
  const overlapping = intervals
    .filter(iv => iv.start < wEnd && iv.end > wStart)
    .sort((a, b) => a.start - b.start);

  if (!overlapping.length) { return [wl]; }

  const result: Worklog[] = [];
  let cursor = wStart;

  for (const iv of overlapping) {
    const segEnd = Math.min(iv.start, wEnd);
    if (segEnd > cursor) {
      result.push(sliceWorklog(wl, cursor, segEnd));
    }
    cursor = Math.max(cursor, iv.end);
    if (cursor >= wEnd) { break; }
  }

  if (cursor < wEnd) {
    result.push(sliceWorklog(wl, cursor, wEnd));
  }

  return result;
}

/**
 * Carves meeting ranges into the worklog track so that, during a meeting, the
 * timeline shows a dedicated meeting block (mode: 'meeting') instead of the
 * underlying computer/manual time. Overlapping worklog segments are split and
 * the meeting periods are removed from them, then meeting blocks are inserted.
 * The result is a single, non-overlapping, start-sorted track.
 *
 * Meeting time always wins for the overlapping period (see STAFF-145177), and a
 * separate block is produced for each meeting range even when the surrounding
 * project/task is identical.
 */
export function mergeMeetingsIntoWorklog(worklog: Worklog[], meetings: MeetingLog[]): Worklog[] {
  if (!meetings?.length) { return worklog || []; }

  // Precompute worklog bounds once: reused for both project/task inheritance
  // and the carve-out below.
  const bounds: WorklogBounds[] = (worklog || []).map((wl) => {
    const start = DateTime.fromISO(wl.start).toMillis();
    return { wl, start, end: start + (wl.time * 1000) };
  });

  const meetingWls: Worklog[] = [];
  const intervals: Array<{ start: number, end: number }> = [];

  for (const meeting of meetings) {
    if (!meeting?.start || !meeting.time) { continue; }
    // Ignore very short meetings (< 5 min): skipping them here means no meeting
    // block is drawn and the underlying worklog is left uncarved, so the normal
    // computer/manual time flows through instead.
    if (meeting.time < minMeetingSeconds) { continue; }
    const start = DateTime.fromISO(meeting.start);
    const end = start.plus({ seconds: meeting.time });
    if (!start.isValid || !end.isValid || end <= start) { continue; }

    const startMs = start.toMillis();
    const endMs = end.toMillis();
    intervals.push({ start: startMs, end: endMs });
    meetingWls.push(meetingToWorklog(meeting, pickOverlappingWorklog(startMs, endMs, bounds)));
  }

  if (!meetingWls.length) { return worklog || []; }

  const carved: Worklog[] = [];
  for (const b of bounds) {
    carved.push(...subtractMeetingIntervals(b.wl, b.start, b.end, intervals));
  }

  return [...carved, ...meetingWls]
    .sort((a, b) => DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis());
}

export function insertOrUpdateWorklog(userWorklogs: Worklog[], newWorklog: Worklog) {
  const newWorklogStart = DateTime.fromISO(newWorklog.start);
  const newWorklogEnd = newWorklogStart.plus({ seconds: newWorklog.time });

  for (const [index, userWl] of userWorklogs.entries()) {
    const userWlStart = DateTime.fromISO(userWl.start);
    const userWlEnd = userWlStart.plus({ seconds: userWl.time });

    if (newWorklogStart >= userWlStart
      && newWorklogStart <= userWlEnd
      && newWorklog.taskId === userWl.taskId
      && newWorklog.mode === userWl.mode) {
      const extendedTime = Math.abs(userWlStart.diff(newWorklogEnd).as('seconds'));
      const newWl = { ...userWl, time: extendedTime };
      userWorklogs.splice(index, 1, newWl);
      return;
    }
  }

  userWorklogs.push(newWorklog);
}
